import { useTvtZone } from "../hooks/useTvtZone.js";

/**
 * Live §F.4 Price-Band Zone indicator. Renders one of three pills:
 *   - Premium  (gold)    — market price > intrinsic; treasury selling
 *   - Neutral  (slate)   — within ±10% of intrinsic; no execution
 *   - Discount (emerald) — market price < intrinsic; treasury buying
 *
 * Falls back to a muted "Calibrating" pill when the backend reports a
 * benign revert (IntrinsicValueUnavailable / NoObservation, i.e. the
 * TWAP window hasn't elapsed yet on a fresh deploy).
 */
export default function ZoneIndicator({ compact = false }) {
    const { data, isLoading } = useTvtZone();
    const zone = data?.zone;
    const palette = TONE[zone] || TONE.Calibrating;
    const label = zone || (isLoading ? "—" : "Calibrating");

    // Spot ÷ TVG ratio → the EXACT §F action inside the zone, so "Premium"
    // resolves to Skimming (0.5%/hr) vs Harvesting (2%/hr) rather than
    // lumping both together.
    const spot = Number(data?.spotPrice || 0);
    const intrinsic = Number(data?.intrinsicValuePerGil || 0);
    const ratio = spot > 0 && intrinsic > 0 ? spot / intrinsic : null;
    const action = keeperAction(ratio);

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${palette.bg} ${palette.ring} ring-1`}
            title={
                data?.spotPrice
                    ? `Spot ${formatPrice(data.spotPrice)} · Intrinsic ${formatPrice(data.intrinsicValuePerGil)}` +
                      (action ? ` · ${action.desc}` : "")
                    : "Awaiting first executor observation"
            }
        >
            <span className={`h-2 w-2 rounded-full ${palette.dot}`} aria-hidden="true" />
            <span className={`font-manrope font-semibold ${palette.text} ${compact ? "text-[10px]" : "text-[11px]"} uppercase tracking-widest`}>
                {compact ? label : `Zone · ${label}`}
                {action ? ` · ${action.label}` : ""}
            </span>
            {ratio != null ? (
                <span className={`font-manrope font-medium ${palette.text} opacity-70 ${compact ? "text-[9px]" : "text-[10px]"}`}>
                    {ratio.toFixed(2)}×
                </span>
            ) : null}
        </span>
    );
}

// Shared §F band → action mapping (mirrors the keeper's zone routing). Exported
// so every surface (Protocol Health, My Tokens) labels the state identically.
export function keeperAction(ratio) {
    if (ratio == null) return null;
    if (ratio < 0.85) return { label: "Buying", desc: "Below floor — buying $GILD to defend it" };
    if (ratio < 1.2) return { label: "Holding", desc: "Near fair value — no trades" };
    if (ratio < 2.0) return { label: "Skimming", desc: "Premium — selling slowly (0.5%/hr) toward TVG" };
    return { label: "Harvesting", desc: "Rich premium — selling fast (2%/hr) toward TVG" };
}

const TONE = {
    Premium: {
        bg: "bg-[#FBC206]/15",
        ring: "ring-[#FBC206]/50",
        dot: "bg-[#FBC206]",
        text: "text-[#b98c23]"
    },
    Neutral: {
        bg: "bg-[#102712]/[0.06]",
        ring: "ring-[#102712]/20",
        dot: "bg-[#102712]/70",
        text: "text-[#102712]"
    },
    Discount: {
        bg: "bg-[#22C55E]/15",
        ring: "ring-[#22C55E]/50",
        dot: "bg-[#22C55E]",
        text: "text-[#2e7a3a]"
    },
    Calibrating: {
        bg: "bg-[#102712]/[0.05]",
        ring: "ring-[#102712]/15",
        dot: "bg-[#102712]/40",
        text: "text-[#102712]/70"
    }
};

// The executor reports spot + intrinsic as USDC-per-$GILD × 1e8 (NOT 1e20 — the
// old divisor collapsed every value to "$0.000000"). These figures are tiny, so
// scale the decimals to keep ~3 significant figures and never emit scientific
// notation (users can't read "1.02e-6").
function formatPrice(value) {
    if (value == null) return "—";
    const n = Number(value) / 1e8;
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) {
        const decimals = Math.min(20, Math.max(6, 2 - Math.floor(Math.log10(n))));
        return `$${n.toFixed(decimals)}`;
    }
    return `$${n.toFixed(6)}`;
}
